import React, { MouseEvent, useEffect, useRef, useState } from "react";
import classNames from "classnames";
import { Link } from "react-router-dom";
import { MatrixHero } from "../../common/components/MatrixHero";
import { TypewriterText } from "../../common/components/TypewriterText";
import FallingIconsBackground from "../../common/components/FallingIconsBackground";
import { useI18n } from "../../i18n/I18nProvider";
import "../../scss/project_category.scss";
import {
    getProjectCategoryById,
    type ProjectCategoryId,
} from "./projectCategoryData";

function useInView(threshold = 0.12) {
    const ref = useRef<HTMLDivElement>(null);
    const [inView, setInView] = useState(false);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setInView(true);
                    if (ref.current) observer.unobserve(ref.current);
                }
            },
            { threshold }
        );

        if (ref.current) observer.observe(ref.current);
        return () => observer.disconnect();
    }, [threshold]);

    return { ref, inView };
}

interface ProjectCategoryPageProps {
    categoryId: ProjectCategoryId;
}

const ProjectCategoryPage: React.FC<ProjectCategoryPageProps> = ({ categoryId }) => {
    const { t } = useI18n();
    const [ready, setReady] = useState(false);
    const category = getProjectCategoryById(categoryId);
    const { ref: introRef, inView: introInView } = useInView(0.15);
    const { ref: listRef, inView: listInView } = useInView(0.08);

    useEffect(() => {
        requestAnimationFrame(() => setReady(true));
    }, []);

    const handleMouseMove = (e: MouseEvent<HTMLElement>) => {
        const target = e.currentTarget as HTMLElement;
        const rect = target.getBoundingClientRect();
        target.style.setProperty("--mouse-x", `${e.clientX - rect.left}px`);
        target.style.setProperty("--mouse-y", `${e.clientY - rect.top}px`);
    };

    if (!category) return null;

    return (
        <section
            className={classNames("project-topic-page", {
                "project-topic-page--ready": ready,
            })}
            onMouseMove={handleMouseMove}
            aria-label={t(category.titleKey)}
        >
            <FallingIconsBackground imageSrc={category.backgroundIcon} imageAlt={t(category.titleKey)} />
            <div className="grid-overlay" aria-hidden="true" />
            <div className="grid-glow" aria-hidden="true" />

            <MatrixHero
                title={t(category.titleKey)}
                subtitle={t(category.subtitleKey)}
                density={0.35}
            />

            <div className="project-topic-page__inner">
                <article
                    ref={introRef}
                    className={classNames("project-topic-card", {
                        "project-topic-card--visible": introInView,
                    })}
                >
                    <div className="project-topic-card__glow" aria-hidden="true" />
                    <div className="project-topic-card__iconWrap">
                        <img
                            src={category.backgroundIcon}
                            alt={t(category.titleKey)}
                            className="project-topic-card__icon"
                            draggable={false}
                        />
                    </div>
                    <div className="project-topic-card__content">
                        <h2 className="project-topic-card__title">{t("projects_topic_scope_title")}</h2>
                        <p className="project-topic-card__text">
                            <TypewriterText text={t(category.summaryKey)} active={introInView} speed={4} />
                        </p>
                        <div className="project-topic-card__actions">
                            <Link to="/projekty/aktualne" className="project-topic-card__backLink">
                                {t("projects_topic_back")}
                            </Link>
                        </div>
                    </div>
                </article>

                <section
                    ref={listRef}
                    className={classNames("project-topic-list", {
                        "project-topic-list--visible": listInView,
                    })}
                >
                    <h2 className="project-topic-list__title">{t("projects_topic_items_title")}</h2>
                    <div className="project-topic-list__grid">
                        {category.items.map((item, index) => (
                            <article
                                key={`${category.id}-${index}`}
                                className="project-topic-item"
                                style={{ animationDelay: `${index * 0.08}s` }}
                            >
                                <h3 className="project-topic-item__title">{t(item.titleKey)}</h3>
                                <div className="project-topic-item__body">
                                    {t(item.descKey)
                                        .split("\n\n")
                                        .map((paragraph, paragraphIndex) => (
                                            <p key={paragraphIndex} className="project-topic-item__text">
                                                {paragraph}
                                            </p>
                                        ))}
                                </div>
                            </article>
                        ))}
                    </div>
                </section>
            </div>
        </section>
    );
};

export default ProjectCategoryPage;
